import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import CompanyPicker from '../../components/CompanyPicker';
import LifecycleShell from '../../components/LifecycleShell';
import { useCurrentUser } from '../../contexts/CurrentUserContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { translations } from '../../translations';
import { describeApiError, fillTemplate } from '../../utils/format';
import { requestJson } from '../../utils/lifecycle';

const PACKAGE_NAME = /^[A-Za-z][A-Za-z0-9_]*(\.[A-Za-z][A-Za-z0-9_]*)+$/;
const HEX_COLOR = /^#[0-9A-Fa-f]{6}$/;
const FILE_SLOTS = ['logo', 'icon', 'splash', 'guideline', 'other'];
const ACCEPTED_FILES = '.png,.jpg,.jpeg,.webp,.gif,.svg,.pdf,.zip,.apk,.aab,.fig,.ai,.psd,.doc,.docx,.xls,.xlsx,.txt';
const STEPS = ['stepCompany', 'stepApp', 'stepAssets'];
const EMPTY_FORM = { appName: '', packageName: '', primaryColor: '', secondaryColor: '', banks: '', notes: '' };

const splitBanks = (text) => text.split(',').map((bank) => bank.trim()).filter(Boolean);

function validate(form, t) {
  const errors = {};
  if (!form.appName.trim()) errors.appName = t.appNameRequired;
  if (form.packageName.trim() && !PACKAGE_NAME.test(form.packageName.trim())) errors.packageName = t.invalidPackage;
  ['primaryColor', 'secondaryColor'].forEach((field) => {
    if (form[field].trim() && !HEX_COLOR.test(form[field].trim())) errors[field] = t.invalidColor;
  });
  return errors;
}

function ColorField({ id, label, value, error, onChange, hint }) {
  const valid = HEX_COLOR.test(value.trim());
  return <div className="field">
    <label className="label" htmlFor={id}>{label} <small>({hint})</small></label>
    <div className="color-field">
      <input type="color" aria-label={label} value={valid ? value.trim() : '#000000'} onChange={(event) => onChange(event.target.value)} />
      <input id={id} className="input" value={value} placeholder="#0054E6" onChange={(event) => onChange(event.target.value)} aria-invalid={Boolean(error)} aria-describedby={error ? `${id}-error` : undefined} />
    </div>
    {error && <p id={`${id}-error`} className="error-text" role="alert">{error}</p>}
  </div>;
}

export default function NewRequestPage() {
  const { language } = useLanguage();
  const t = translations[language];
  const trail = [{ label: t.listTitle, to: '/custom-apps' }, { label: t.wizardTitle }];
  const navigate = useNavigate();
  const { can } = useCurrentUser();
  const [step, setStep] = useState(1);
  const [company, setCompany] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [files, setFiles] = useState({});
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  if (!can('custom_app:create')) {
    return <LifecycleShell title={t.wizardTitle} eyebrow={t.wizardEyebrow} trail={trail}>
      <div className="notice notice--error" role="alert"><span>{t.errorCode.insufficient_role}</span><Link to="/custom-apps">{t.listTitle}</Link></div>
    </LifecycleShell>;
  }

  const setField = (name) => (value) => setForm((previous) => ({ ...previous, [name]: value }));
  const onInput = (name) => (event) => setField(name)(event.target.value);

  const goNext = () => {
    if (step === 2) {
      const found = validate(form, t);
      setErrors(found);
      if (Object.keys(found).length) return;
    }
    setStep((current) => current + 1);
  };

  const submit = async (event) => {
    event.preventDefault();
    // Enter inside a text field submits the form; only the last step may send the request.
    if (step < STEPS.length) {
      goNext();
      return;
    }
    setSubmitting(true);
    setSubmitError('');
    try {
      const created = await requestJson('/custom-apps', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          crmCompanyId: company.id,
          company: { id: company.id, name: company.name, code: company.code || null },
          appName: form.appName.trim(),
          packageName: form.packageName.trim() || null,
          primaryColor: form.primaryColor.trim() || null,
          secondaryColor: form.secondaryColor.trim() || null,
          banks: splitBanks(form.banks),
          notes: form.notes.trim() || null,
        }),
      });

      // The request exists from here on, so a file that fails is reported rather than undoing it.
      const failed = [];
      for (const [category, file] of Object.entries(files)) {
        const body = new FormData();
        body.append('file', file);
        body.append('category', category);
        try {
          await requestJson(`/custom-apps/${created.id}/assets`, { method: 'POST', body });
        } catch (error) {
          if (error.status === 401) return;
          failed.push(file.name);
        }
      }
      navigate(`/custom-apps/${created.id}`, { state: failed.length ? { uploadFailures: failed } : undefined });
    } catch (error) {
      if (error.status !== 401) setSubmitError(describeApiError(error, t, t.requestFailed));
      setSubmitting(false);
    }
  };

  const filesChosen = Object.keys(files).length;

  return <LifecycleShell title={t.wizardTitle} eyebrow={t.wizardEyebrow} trail={trail}>
    <div className="split split--aside">
      <section className="card" aria-labelledby="wizard-step-heading">
        <form className="stack stack--lg" onSubmit={submit} noValidate>
          <div className="stack stack--sm">
            <div className="segments" aria-hidden="true">
              {STEPS.map((key, index) => <span key={key} className={`segments__bar${index < step ? ' is-filled' : ''}`} />)}
            </div>
            <p className="hint">{fillTemplate(t.stepOf, { step, total: STEPS.length })}</p>
            <h2 id="wizard-step-heading">{t[STEPS[step - 1]]}</h2>
          </div>

          {step === 1 && <div className="stack">
            <p className="hint">{t.companyHelp}</p>
            <CompanyPicker selected={company} onSelect={setCompany} />
            {!company && <p className="hint">{t.companyChooseFirst}</p>}
          </div>}

          {step === 2 && <div className="form-grid">
            <div className="field">
              <label className="label" htmlFor="app-name">{t.appNameLabel}</label>
              <input id="app-name" className="input" value={form.appName} onChange={onInput('appName')} aria-invalid={Boolean(errors.appName)} aria-describedby={errors.appName ? 'app-name-error' : undefined} maxLength={100} required />
              {errors.appName && <p id="app-name-error" className="error-text" role="alert">{errors.appName}</p>}
            </div>
            <div className="field">
              <label className="label" htmlFor="package-name">{t.packageNameLabel} <small>({t.optional})</small></label>
              <input id="package-name" className="input" value={form.packageName} onChange={onInput('packageName')} placeholder="id.co.school.app" aria-invalid={Boolean(errors.packageName)} aria-describedby="package-name-hint" />
              <p id="package-name-hint" className={errors.packageName ? 'error-text' : 'hint'}>{errors.packageName || t.packageNameHint}</p>
            </div>
            <ColorField id="primary-color" label={t.primaryColorLabel} hint={t.optional} value={form.primaryColor} error={errors.primaryColor} onChange={setField('primaryColor')} />
            <ColorField id="secondary-color" label={t.secondaryColorLabel} hint={t.optional} value={form.secondaryColor} error={errors.secondaryColor} onChange={setField('secondaryColor')} />
            <div className="field field--wide">
              <label className="label" htmlFor="banks">{t.bankLabel} <small>({t.optional})</small></label>
              <input id="banks" className="input" value={form.banks} onChange={onInput('banks')} placeholder="BSI, BNI" aria-describedby="banks-hint" />
              <p id="banks-hint" className="hint">{t.bankHint}</p>
            </div>
            <div className="field field--wide">
              <label className="label" htmlFor="notes">{t.notesLabel} <small>({t.optional})</small></label>
              <textarea id="notes" className="textarea" rows="4" value={form.notes} onChange={onInput('notes')} placeholder={t.notesPlaceholder} maxLength={4000} />
            </div>
          </div>}

          {step === 3 && <div className="stack">
            <p className="hint">{t.filesHelp}</p>
            <div className="form-grid">
              {FILE_SLOTS.map((category) => <div className="field" key={category}>
                <label className="label" htmlFor={`file-${category}`}>{t.categoryLabel[category]}</label>
                <input
                  id={`file-${category}`}
                  type="file"
                  accept={ACCEPTED_FILES}
                  onChange={(event) => {
                    const file = event.target.files[0];
                    setFiles((previous) => {
                      const next = { ...previous };
                      if (file) next[category] = file; else delete next[category];
                      return next;
                    });
                  }}
                />
              </div>)}
            </div>
            {submitError && <div className="notice notice--error" role="alert">{submitError}</div>}
          </div>}

          <div className="btn-row btn-row--split">
            {step > 1
              ? <button className="btn" type="button" onClick={() => setStep(step - 1)} disabled={submitting}>{t.back}</button>
              : <Link className="btn" to="/custom-apps">{t.cancel}</Link>}
            {step < STEPS.length
              ? <button className="btn btn--primary" type="button" onClick={goNext} disabled={step === 1 && !company}>{t.next}</button>
              : <button className="btn btn--primary" type="submit" disabled={submitting}>{submitting ? t.sendingRequest : t.sendRequest}</button>}
          </div>
        </form>
      </section>

      <aside className="card stack" aria-label={t.summaryTitle}>
        <h2>{t.summaryTitle}</h2>
        <dl className="facts">
          {[
            [t.company, company?.name],
            [t.appNameLabel, form.appName.trim()],
            [t.packageNameLabel, form.packageName.trim()],
            [t.tabAssets, filesChosen || ''],
          ].map(([label, value]) => <div key={label}><dt>{label}</dt><dd className={value ? '' : 'is-empty'}>{value || t.summaryEmpty}</dd></div>)}
        </dl>
        <p className="callout">{t.summaryNote}</p>
      </aside>
    </div>
  </LifecycleShell>;
}
